import { useEffect } from 'react';
import { useLocation, Navigate, Outlet } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import usePermissions from '@hooks/usePermissions';
import { alertActions } from '@actions';
import { useTranslation } from '@hooks';
import { RoutePath } from '@constants/routes';
import { normalizedPathname } from '@helpers/navigation';

const BILLING_EXPIRED_ROUTES = [
    RoutePath.AccountSettingsGeneral,
    RoutePath.AccountSettingsBilling,
    RoutePath.AccountSettingsBrands,
    RoutePath.AccountSettingsProfile,
    RoutePath.AccountSettingsUsers,
    RoutePath.AccountSettingsTeams,
    RoutePath.AccountSettingsChannels,
];

const MODULE_ROUTES = [
    { base: RoutePath.Contacts, module: 'contacts' },
    { base: RoutePath.Locations, module: 'locations' },
    { base: RoutePath.Reviews, module: 'reviews' },
    { base: RoutePath.Social, module: 'posts' },
    { base: RoutePath.Messaging, module: 'messaging' },
    { base: RoutePath.WebPages, module: 'microsites' },
    { base: RoutePath.Surveys, module: 'surveys' },
    { base: RoutePath.Campaigns, module: 'campaigns' },
    { base: RoutePath.Coupons, module: 'coupons' },
];

export const PrivateRoute = () => {
    const location = useLocation();
    const pathname = normalizedPathname(location.pathname);
    const auth = useSelector(state => state.auth);
    const account = useSelector(state => state.account.account);
    const { hasAccess, hasFullAccess, hasSettingsAccess, hasFullSettings } = usePermissions();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const forbiddenNoSettingsAccess =
        auth.user &&
        account &&
        !hasSettingsAccess() &&
        pathname.startsWith(RoutePath.AccountSettings) &&
        pathname !== RoutePath.AccountSettings &&
        pathname !== RoutePath.AccountSettingsProfile;

    const forbiddenNoFullSettings =
        auth.user &&
        account &&
        !hasFullSettings() &&
        (pathname === RoutePath.AccountSettingsGeneral ||
            pathname === RoutePath.AccountSettingsBilling ||
            pathname === RoutePath.AccountSettingsIntegrations ||
            pathname === RoutePath.AccountSettingsBrands ||
            pathname === RoutePath.AccountSettingsChannels ||
            pathname === RoutePath.AccountSettingsTeams);

    useEffect(() => {
        if (forbiddenNoSettingsAccess || forbiddenNoFullSettings) {
            dispatch(alertActions.error(t('errors.forbid')));
        }
    }, [forbiddenNoSettingsAccess, forbiddenNoFullSettings]);

    if (!auth.user && !auth.loading) {
        return <Navigate to={RoutePath.Login} state={{ from: location }} replace />;
    }

    if (!account && !pathname.startsWith(RoutePath.Onboarding)) return null;

    if (auth.user && account) {
        const productsMaxNumberExceeded =
            account &&
            (account?.companiesMaxCount < account?.companies?.length ||
                account?.usersMaxCount < account?.users?.length);
        const billingExpired = account && account?.billingDiffInSeconds < 0;

        // INFO: Redirect to show billing
        if (
            hasFullSettings() &&
            !BILLING_EXPIRED_ROUTES.some(r => r === pathname) &&
            pathname !== RoutePath.LocationsManage &&
            (productsMaxNumberExceeded || billingExpired)
        ) {
            return <Navigate to={RoutePath.AccountSettingsBilling} replace />;
        }
        // INFO: Redirect user without settings access from settings pages except /profile
        if (forbiddenNoSettingsAccess) {
            return <Navigate to={RoutePath.AccountSettingsProfile} replace />;
        }
        // INFO: Redirect users without full settings from restricted settings pages
        if (forbiddenNoFullSettings) {
            return <Navigate to={RoutePath.AccountSettingsProfile} replace />;
        }
        // INFO: Redirect users away from features they have no access to (tier = None)
        const moduleRoute = MODULE_ROUTES.find(r => pathname.startsWith(r.base));
        if (moduleRoute && !hasAccess(moduleRoute.module)) {
            return <Navigate to={RoutePath.Home} replace />;
        }
        // INFO: Pulse AI requires full messaging access
        if (pathname.startsWith(RoutePath.MessagingPulseAI) && !hasFullAccess('messaging')) {
            return <Navigate to={RoutePath.MessagingInbox} replace />;
        }
        // INFO: Redirect everyone to the onboarding if it isn't completed
        if (!account.onboardingCompleted && !pathname.startsWith(RoutePath.Onboarding)) {
            return <Navigate to={RoutePath.Onboarding} replace />;
        }
        // INFO: Redirect everyone from the onboarding if it's completed
        if (account.onboardingCompleted && pathname.startsWith(RoutePath.Onboarding)) {
            return <Navigate to={RoutePath.Home} replace />;
        }
        // INFO: Redirect everyone from to paywall wrapper routes if advanced features are locked
        // and vice versa
        if (pathname.startsWith(RoutePath.Campaigns)) {
            if (!account?.campaignsEnabled && pathname !== RoutePath.Campaigns) {
                return <Navigate to={RoutePath.Campaigns} replace />;
            }
            if (account?.campaignsEnabled && pathname === RoutePath.Campaigns) {
                return <Navigate to={RoutePath.CampaignsOneTime} replace />;
            }
        }
        if (pathname.startsWith(RoutePath.Surveys)) {
            if (!account?.surveysEnabled && pathname !== RoutePath.Surveys) {
                return <Navigate to={RoutePath.Surveys} replace />;
            }
            if (account?.surveysEnabled && pathname === RoutePath.Surveys) {
                return <Navigate to={RoutePath.SurveysResponses} replace />;
            }
        }
    }

    return <Outlet />;
};

export default PrivateRoute;
